import { motion } from 'framer-motion';
import { X, CheckCircle2, CreditCard, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useModal } from '../../../hooks/modalContext';
import { useAuth } from '../../../../auth/Auth';
import { usePaymentOptions } from '../../../hooks/PaymentOptionsContext';
import { updateTenantSubscriptionPaymentMethod } from '../../../../auth/_requests';
import { showToast } from '../../../utils/showToast';
import { isPaymentOptionActive, PaymentOption } from '../../../utils/types';
import { ButtonSpinner } from '../../../UI/ButtonSpinner/ButtonSpinner';
import visa from '../../../../assets/pages/payment/visa.png';
import mastercard from '../../../../assets/pages/payment/mastercard.png';
import amex from '../../../../assets/pages/payment/amex.png';

const UpdateSubscriptionPaymentMethod = () => {
  const { modalState, setModal } = useModal();
  const { auth } = useAuth();
  const { paymentOptions, loading: optionsLoading } = usePaymentOptions();

  const { tenantMitSubscriptionId, currentTenantPaymentOptionId, onUpdated } = modalState || {};

  const [selectedOptionId, setSelectedOptionId] = useState<string>('');
  const [updating, setUpdating] = useState(false);

  // Filter only active payment options
  const activeOptions = paymentOptions.filter((opt) => isPaymentOptionActive(opt.status));

  useEffect(() => {
    if (activeOptions.length > 0) {
      if (
        currentTenantPaymentOptionId &&
        activeOptions.some((opt) => opt.id === currentTenantPaymentOptionId)
      ) {
        setSelectedOptionId(currentTenantPaymentOptionId);
      } else {
        setSelectedOptionId(activeOptions[0].id);
      }
    }
  }, [currentTenantPaymentOptionId, paymentOptions]);

  const getCardIcon = (brand: string | null, method: string) => {
    const brandLower = (brand || method || '').toLowerCase();
    if (brandLower.includes('visa')) return visa;
    if (brandLower.includes('master')) return mastercard;
    if (brandLower.includes('amex') || brandLower.includes('american')) return amex;
    return null;
  };

  const getPaymentOptionTitle = (opt: PaymentOption) => {
    if (opt.paymentMethod?.toLowerCase() === 'preauthorizeddebit') {
      return opt.bankName || 'Pre-authorized debit';
    }
    return opt.cardBrand ? `${opt.cardBrand}` : opt.paymentMethod;
  };

  const getPaymentOptionSubtitle = (opt: PaymentOption) => {
    if (opt.paymentMethod?.toLowerCase() === 'preauthorizeddebit') {
      return opt.bankAccountNumber
        ? `Account ending in •••• ${opt.bankAccountNumber.slice(-4)}`
        : 'Bank account';
    }
    return opt.cardEndingDigits ? `Ending in •••• ${opt.cardEndingDigits}` : '';
  };

  const handleUpdate = async () => {
    if (!tenantMitSubscriptionId || !selectedOptionId) return;

    try {
      setUpdating(true);
      await updateTenantSubscriptionPaymentMethod(
        auth?.data?.token,
        tenantMitSubscriptionId,
        selectedOptionId
      );
      setUpdating(false);

      localStorage.setItem('selectedPaymentOption', selectedOptionId);

      showToast({
        message: 'Autopay payment method updated successfully',
        type: 'success',
      });

      if (onUpdated) {
        onUpdated();
      }

      // Dispatch event so active components can refresh building details and selected card state
      window.dispatchEvent(
        new CustomEvent('tenantSubscriptionUpdated', {
          detail: { selectedOptionId },
        })
      );

      setModal(null);
    } catch (error) {
      setUpdating(false);
      const message = error instanceof Error
        ? error.message
        : 'Failed to update Autopay payment method. Please try again.';
      showToast({
        message,
        type: 'error',
      });
    }
  };

  return (
    <motion.div
      className='relative w-[480px] max-w-[95vw] bg-white rounded-[16px] flex flex-col overflow-hidden shadow-xl'
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
    >
      {/* Header */}
      <div className='flex items-center justify-between px-6 py-5 border-b border-[#E8EAF0]'>
        <div>
          <h3 className='text-[18px] font-bold text-[#0A0B14]'>Update autopay payment method</h3>
          <p className='text-[13px] text-[#6B7285] mt-0.5'>
            Select an active payment method for your recurring rent payments.
          </p>
        </div>
        <button
          type='button'
          onClick={() => setModal(null)}
          className='p-1 rounded-full text-gray-400 hover:text-gray-600 transition-colors'
        >
          <X size={20} />
        </button>
      </div>

      {/* Body */}
      <div className='p-6 max-h-[400px] overflow-y-auto'>
        {optionsLoading ? (
          <div className='py-8 text-center text-[14px] text-[#6B7285]'>
            Loading active payment methods...
          </div>
        ) : activeOptions.length === 0 ? (
          <div className='py-6 text-center flex flex-col items-center'>
            <CreditCard className='w-12 h-12 text-[#94A3B8] mb-3' />
            <p className='text-[14px] font-medium text-[#0A0B14]'>No active payment methods found</p>
            <p className='text-[13px] text-[#6B7285] mt-1 max-w-[320px]'>
              You don't have any active payment methods. Please add a new active payment method to update Autopay.
            </p>
            <button
              type='button'
              onClick={() => setModal('add-payment-method')}
              className='mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-[10px] bg-[#0055FF] text-white text-[13px] font-semibold hover:bg-[#0044CC] transition-colors'
            >
              <Plus size={16} />
              Add payment method
            </button>
          </div>
        ) : (
          <div className='space-y-3'>
            {activeOptions.map((opt) => {
              const isSelected = selectedOptionId === opt.id;
              const cardLogo = getCardIcon(opt.cardBrand, opt.paymentMethod);

              return (
                <div
                  key={opt.id}
                  onClick={() => setSelectedOptionId(opt.id)}
                  className={`relative cursor-pointer rounded-[12px] border p-4 transition-all duration-200 flex items-center justify-between ${
                    isSelected
                      ? 'border-[#0055FF] bg-[#EEF5FF] shadow-sm'
                      : 'border-[#E8EAF0] bg-white hover:border-[#C5CAD6]'
                  }`}
                >
                  <div className='flex items-center gap-3.5'>
                    <div className='w-10 h-7 rounded-[6px] border border-[#E8EAF0] bg-white flex items-center justify-center overflow-hidden p-1 shrink-0'>
                      {cardLogo ? (
                        <img src={cardLogo} alt='card' className='max-h-full object-contain' />
                      ) : (
                        <CreditCard size={18} className='text-[#6B7285]' />
                      )}
                    </div>
                    <div>
                      <div className='flex items-center gap-2'>
                        <span className='text-[14px] font-semibold text-[#0A0B14]'>
                          {getPaymentOptionTitle(opt)}
                        </span>
                        {opt.isDefault && (
                          <span className='text-[10px] font-bold px-2 py-0.5 rounded-[4px] bg-[#E2E8F0] text-[#475569] uppercase tracking-wider'>
                            Default
                          </span>
                        )}
                      </div>
                      <div className='text-[12px] text-[#6B7285] mt-0.5'>
                        {getPaymentOptionSubtitle(opt)}
                      </div>
                    </div>
                  </div>

                  <div className='shrink-0 pl-3'>
                    <div
                      className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'border-[#0055FF] bg-[#0055FF]'
                          : 'border-[#CBD5E1] bg-white'
                      }`}
                    >
                      {isSelected && <CheckCircle2 className='w-4 h-4 text-white' />}
                    </div>
                  </div>
                </div>
              );
            })}

            <div className='pt-2'>
              <button
                type='button'
                onClick={() => setModal('add-payment-method')}
                className='w-full py-2.5 px-4 rounded-[10px] border border-dashed border-[#CBD5E1] text-[#0055FF] text-[13px] font-semibold hover:bg-[#F8FAFC] transition-colors flex items-center justify-center gap-2'
              >
                <Plus size={16} />
                Add new payment method
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className='flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E8EAF0] bg-[#F8FAFC]'>
        <button
          type='button'
          onClick={() => setModal(null)}
          className='px-4 py-2.5 rounded-[10px] border border-[#E8EAF0] bg-white text-[13px] font-semibold text-[#6B7285] hover:text-[#0A0B14] transition-colors'
        >
          Cancel
        </button>
        <button
          type='button'
          disabled={!selectedOptionId || activeOptions.length === 0 || updating}
          onClick={handleUpdate}
          className='px-5 py-2.5 rounded-[10px] bg-[#0055FF] text-white text-[13px] font-semibold hover:bg-[#0044CC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2'
        >
          {updating && <ButtonSpinner />}
          Save payment method
        </button>
      </div>
    </motion.div>
  );
};

export { UpdateSubscriptionPaymentMethod };
